import {
  Box,
  NextImage,
  dropDetails,
  useBreakpointValue,
  NextLink,
} from '@dapperlabs/core-fe';
import { useHover } from '@dapperlabs/skeleton-ui';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { useTranslation } from 'src/lib/translations';
import { PackPriceLabel } from 'src/modules/PackDetailsPage';

import { CTAButton } from './CTAButton';
import { PackHeading } from './PackHeading';
interface Props {
  cardBg: string;
  cardImg: string;
  distributionId: number;
  packPrice: number;
  packTitle: string;
  tier: string;
  totalPacks: number;
}

const slideUp = {
  closed: { transition: { type: 'tween' }, y: '0rem' },
  open: { transition: { type: 'tween' }, y: '-4rem', zIndex: 2 },
};

const PreviousPackItem = ({
  tier,
  distributionId,
  packTitle,
  packPrice,
  cardBg,
  cardImg,
  totalPacks,
}: Props) => {
  const { t } = useTranslation();

  const isMdUp = useBreakpointValue({ md: true });
  const [hoverRef, isHovered] = useHover();

  return (
    <Box
      ref={hoverRef}
      overflow='hidden'
      width={['100%', '100%', '100%', '100%', '384px']}
      border='1px solid #393B45'
      borderRadius='20px'
      cursor='pointer'
    >
      <NextLink href={dropDetails(distributionId)} legacyBehavior passHref>
        <Box
          sx={{
            aspectRatio: '3 / 2',
          }}
          position='relative'
          alignItems='center'
          justifyContent='center'
          display='flex'
          width={['100%']}
          backgroundImage={cardBg}
          backgroundSize='100% auto'
          backgroundPosition='center'
          backgroundRepeat='no-repeat'
          borderTopRadius='20px'
        >
          <NextImage
            objectFit='cover'
            alt='pack image'
            layout='fill'
            src={cardImg}
          />
        </Box>
      </NextLink>

      <Box
        as={motion.div}
        justifyContent='space-between'
        flexDirection='column'
        display='flex'
        px={[4, 4, 8]}
        animate={isHovered && isMdUp ? 'open' : 'closed'}
        bgColor='rgba(0,0,0,0.7)'
        variants={slideUp}
      >
        <NextLink href={dropDetails(distributionId)} legacyBehavior passHref>
          <Box py={[6, 7]}>
            <PackHeading
              packTitle={packTitle}
              tier={tier}
              totalPacks={totalPacks}
            />

            <PackPriceLabel priceTag={packPrice} />
          </Box>
        </NextLink>

        <AnimatePresence>
          {isHovered && isMdUp && (
            <motion.div
              key={`view-cta-${distributionId}`}
              transition={{ delay: 0.15 }}
              animate={{
                height: 'auto',
                opacity: 1,
                visibility: 'visible',
                y: '0%',
                zIndex: 2,
              }}
              exit={{
                opacity: 0,
              }}
              initial={{
                height: 0,
                opacity: 0,
                visibility: 'hidden',
                y: '100%',
              }}
            >
              <Box pos='absolute' right={0} left={0}>
                <CTAButton
                  href={dropDetails(distributionId)}
                  label={t('UpcomingDrops.PackDropButtonText')}
                />
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export { PreviousPackItem };
